import React, { useState } from "react";
import { I } from "../../Components/CMI/icons";
import { KPI, Badge } from "../../Components/CMI/primitives";
import PageHead, { SectionHead, Footer } from "../../Layouts/CMI/PageHead";
import { useCMI } from "../../Layouts/CMI/cmi-context";

const AUTOMATIONS = [
  { id: "fin-daily",     title: "Resumen Financiero Diario", desc: "Briefing matinal con ingresos, gastos, saldos y facturas vencidas.",   icon: "ChartLine", tone: "primary", schedule: "Cada día · 08:00",       sources: "Finanzas · Todas las empresas", recipients: "3 destinatarios", last: "Hoy · 08:00",     on: true },
  { id: "mkt-weekly",    title: "Informe Marketing Semanal", desc: "Análisis de leads, conversión, fuentes y coste por adquisición.",      icon: "Megaphone", tone: "cyan",    schedule: "Cada lunes · 09:00",      sources: "Marketing · Visados, Permiso, CVS", recipients: "5 destinatarios", last: "Lun · 09:00", on: true },
  { id: "ops-weekly",    title: "Operativa Semanal",          desc: "Cuellos de botella, proyectos pendientes y tiempos por estado.",       icon: "Briefcase", tone: "success", schedule: "Cada viernes · 16:00",    sources: "Proyectos · Todas las empresas", recipients: "4 destinatarios", last: "Vie · 16:00", on: true },
  { id: "alert-venc",    title: "Alertas de Facturas Vencidas", desc: "Aviso cuando una factura cruza 30, 60 o 90 días vencida.",            icon: "Bell",      tone: "warning", schedule: "En tiempo real",          sources: "Finanzas · Visados", recipients: "Equipo Finanzas", last: "Hace 2h",     on: true },
  { id: "anom-fin",      title: "Detección de Anomalías",     desc: "Identificación automática de desviaciones >20% sobre media móvil.",     icon: "Triangle",  tone: "danger",  schedule: "Continuo",                sources: "Finanzas, Operativa", recipients: "Dirección General", last: "Hace 18h",  on: true },
  { id: "junta-mensual", title: "Informe Junta Directiva",     desc: "Compendio mensual con resumen ejecutivo, narrativa y gráficos.",       icon: "FileText",  tone: "primary", schedule: "1º día del mes · 10:00",  sources: "Todas las áreas", recipients: "Junta Directiva (4)", last: "01 May · 10:00", on: false },
];

const TEMPLATES = [
  { icon: "Calendar",  title: "Briefing matinal",        desc: "Estado del día anterior en 5 bullets, antes de empezar la jornada." },
  { icon: "ChartLine", title: "Análisis de tendencias",  desc: "Detección semanal de variaciones significativas en series temporales." },
  { icon: "Triangle",  title: "Detector de anomalías",   desc: "Alerta cuando un KPI sale fuera de la banda de ±2σ histórica." },
  { icon: "FileText",  title: "Informe mensual completo",desc: "Compendio narrativo de todas las áreas con benchmarks y proyecciones." },
  { icon: "Building",  title: "Comparativa interempresa",desc: "Tabla cruzada Visados vs Permiso vs CVS con deltas y semáforos." },
  { icon: "Mail",      title: "Resumen para inversores", desc: "Síntesis trimestral con tono ejecutivo y proyecciones." },
];

const ACTIVITY = [
  { when: "Hoy · 08:00",    type: "primary", icon: "ChartLine", title: "Resumen Financiero Diario",     desc: "Generado y enviado a 3 destinatarios · Visados.", status: "ok" },
  { when: "Hoy · 02:14",    type: "danger",  icon: "Triangle",  title: "Anomalía detectada",             desc: "Factura #115 superó 60 días vencida. Notificado Equipo Finanzas.", status: "alert" },
  { when: "Ayer · 22:30",   type: "warning", icon: "Bell",      title: "Alerta de vencimiento",          desc: "5 facturas cruzaron el umbral de 30 días.", status: "alert" },
  { when: "Ayer · 16:00",   type: "success", icon: "Briefcase", title: "Operativa Semanal",              desc: "Generado y enviado a 4 destinatarios.", status: "ok" },
  { when: "Lun · 09:00",    type: "cyan",    icon: "Megaphone", title: "Informe Marketing Semanal",      desc: "Generado y enviado a 5 destinatarios.", status: "ok" },
  { when: "Lun · 08:00",    type: "primary", icon: "ChartLine", title: "Resumen Financiero Diario",     desc: "Generado y enviado a 3 destinatarios.", status: "ok" },
  { when: "01 May · 10:00", type: "primary", icon: "FileText",  title: "Informe Junta Directiva (abril)", desc: "Generado y enviado a 4 destinatarios.", status: "ok" },
  { when: "30 Abr · 19:42", type: "warning", icon: "Plug",      title: "Integración Search Console",     desc: "Reintento automático fallido. Requiere configuración manual.", status: "warn" },
];

const DashboardIA = () => {
  document.title = "Automatización IA | CMI";
  const { openAI } = useCMI();
  const [autos, setAutos] = useState(() => Object.fromEntries(AUTOMATIONS.map(a => [a.id, a.on])));
  const [genTarget, setGenTarget] = useState("Finanzas");
  const [genPeriod, setGenPeriod] = useState("Hoy");

  const toggle = (id) => setAutos((prev) => ({ ...prev, [id]: !prev[id] }));
  const activeCount = Object.values(autos).filter(Boolean).length;

  const runNow = () => {
    const map = { Finanzas: "finanzas", Marketing: "marketing", Operativa: "proyectos" };
    openAI(map[genTarget] || "finanzas");
  };

  return (
    <>
      <PageHead icon={I.Sparkle} title="Automatización IA" sub="Reportes y análisis generados por IA" />

      <div className="ia-hero">
        <div>
          <div className="lead"><span className="sparkle"><I.Sparkle size={12}/></span>Asistente CMI</div>
          <h1>Convierte cada dashboard en un resumen ejecutivo en 8 segundos.</h1>
          <p className="sub">Genera análisis automáticos del estado financiero, comercial y operativo. Programa briefings recurrentes para tu equipo y recibe alertas cuando algo se desvía de lo esperado.</p>
        </div>
        <div className="ia-hero-side">
          <div className="ia-hero-form">
            <label>Dashboard</label>
            <div className="seg seg-dark">
              {["Finanzas", "Marketing", "Operativa"].map(s => (
                <button key={s} className={genTarget === s ? "active" : ""} onClick={() => setGenTarget(s)}>{s}</button>
              ))}
            </div>
            <label>Periodo</label>
            <div className="seg seg-dark">
              {["Hoy", "Semana", "Mes", "Año"].map(p => (
                <button key={p} className={genPeriod === p ? "active" : ""} onClick={() => setGenPeriod(p)}>{p}</button>
              ))}
            </div>
            <button className="btn btn-ai" onClick={runNow} style={{ marginTop: 6, width: "100%", justifyContent: "center" }}>
              <I.Sparkle size={14}/> Generar resumen ahora
            </button>
          </div>
        </div>
      </div>

      <div className="grid gc-4" style={{ marginTop: 28 }}>
        <KPI tier="primary" tone="primary" icon={I.Sparkle}
          label="Automatizaciones activas" value={String(activeCount)}
          extras={<Badge tone="pos">+2 este mes</Badge>}
        />
        <KPI tier="primary" tone="cyan" icon={I.FileText}
          label="Reportes generados" value="186"
          delta="↑ 24%" deltaTone="pos" deltaRef="vs mes anterior"
        />
        <KPI tier="primary" tone="warning" icon={I.Bell}
          label="Alertas disparadas" value="8"
          extras={<Badge tone="warn">3 sin revisar</Badge>}
        />
        <KPI tier="primary" tone="success" icon={I.Clock}
          label="Tiempo ahorrado" value="14h"
          extras={<span className="kpi-ref">por semana · estimado</span>}
        />
      </div>

      <SectionHead icon={I.Plug} title="Automatizaciones" sub="Reportes programados y alertas en tiempo real"
        right={<button className="btn btn-pri"><I.Plus size={14}/>Nueva automatización</button>}
      />
      <div className="grid gc-3">
        {AUTOMATIONS.map(a => {
          const Ico = I[a.icon];
          const on = autos[a.id];
          return (
            <div className="auto-card" key={a.id}>
              <div className="head">
                <span className={`ic ${a.tone}`}><Ico size={20}/></span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="title">{a.title}</div>
                  <div className="desc">{a.desc}</div>
                </div>
                <div className={`toggle ${on ? "on" : ""}`} onClick={() => toggle(a.id)} role="switch" aria-checked={on}/>
              </div>
              <div className="props">
                <div className="prop"><I.Calendar size={14}/><span className="k">Programación</span><span>{a.schedule}</span></div>
                <div className="prop"><I.ChartLine size={14}/><span className="k">Origen</span><span>{a.sources}</span></div>
                <div className="prop"><I.Mail size={14}/><span className="k">Destinatarios</span><span>{a.recipients}</span></div>
              </div>
              <div className="actions">
                <span className="last-run">{on ? <><span className="dot ok"/> Última ejecución · {a.last}</> : <><span className="dot off"/> Pausada</>}</span>
                <div style={{ display: "flex", gap: 6 }}>
                  <button className="btn btn-ghost btn-icon" title="Ver historial"><I.Eye size={14}/></button>
                  <button className="btn btn-ghost btn-icon" title="Configurar"><I.Settings size={14}/></button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <SectionHead icon={I.Sparkle} title="Plantillas" sub="Empieza desde una receta probada" />
      <div className="grid gc-3">
        {TEMPLATES.map((t, i) => {
          const Ico = I[t.icon];
          return (
            <div key={i} className="tpl-card">
              <div className="h"><Ico size={16}/>{t.title}</div>
              <div className="s">{t.desc}</div>
              <div className="activate"><I.Plus size={12}/>Activar plantilla</div>
            </div>
          );
        })}
      </div>

      <SectionHead icon={I.Clock} title="Actividad reciente" sub="Últimas ejecuciones de automatizaciones y alertas"
        right={<button className="btn btn-sec"><I.Download size={14}/>Exportar registro</button>}
      />
      <div className="ai-feed">
        {ACTIVITY.map((a, i) => {
          const Ico = I[a.icon];
          return (
            <div className="ai-feed-item" key={i}>
              <span className="when">{a.when}</span>
              <div style={{ display: "flex", gap: 12, alignItems: "center", minWidth: 0 }}>
                <span className={`ic ${a.type}`}><Ico size={14}/></span>
                <div style={{ minWidth: 0 }}>
                  <div className="what">{a.title}</div>
                  <div className="desc">{a.desc}</div>
                </div>
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                {a.status === "ok"    && <span className="bdg pos"><span className="dot"/>Enviado</span>}
                {a.status === "alert" && <span className="bdg neg"><span className="dot"/>Alerta</span>}
                {a.status === "warn"  && <span className="bdg warn"><span className="dot"/>Reintentar</span>}
                <button className="btn btn-ghost btn-icon" title="Ver"><I.ArrowRight size={14}/></button>
              </div>
            </div>
          );
        })}
      </div>

      <Footer />
    </>
  );
};

export default DashboardIA;
